import { SharePointData, ExcludedItems } from '../../types/sharepoint.types';
import { fetchLists, fetchDocumentLibraries } from '../../services/api/sharepointService';
// ADD this import at the top
import { URLAnalysis, URLTargetingUtils } from '../../services/api/sharepointService';

export interface SharePointItem {
  id: string;
  title: string;
  name: string;
  display_name: string;
  list_id?: string;
  library_id?: string;
  isChecked: boolean;
  created: Date;
  modified: Date;
}

export const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000;

export const getCachedData = (key: string) => {
  const item = sessionStorage.getItem(key);
  if (!item) return null;
  const { value, timestamp } = JSON.parse(item);
  if (Date.now() - timestamp > CACHE_DURATION) {
    sessionStorage.removeItem(key);
    return null;
  }
  return value;
};

export const updateSharePointItems = (
  itemData: any[], 
  key: string, 
  excluded: ExcludedItems, 
  excludedKey: keyof ExcludedItems, 
  idKey: keyof SharePointItem
) => {
  if (!Array.isArray(itemData)) {
    console.error(`Expected an array for updateSharePointItems ${key}, but got:`, itemData);
    return [];
  }

  return itemData.map((item: any) => {
    let isChecked = true;

    if (key === 'document_libraries') {
      const libraryId = item[idKey];
      
      if (excluded.excluded_document_libraries.includes(libraryId)) {
        isChecked = false;
      } else {
        const partialLibrary = excluded.partial_excluded_document_libraries?.find(
          lib => lib.lib_id === libraryId
        );
        if (partialLibrary && partialLibrary.excluded_docs.length > 0) {
          isChecked = false;
        }
      }
    } else {
      isChecked = !excluded[excludedKey].includes(item[idKey]);
    }

    return {
      ...item,
      checked: isChecked,
    };
  });
};

 
 // CORRECTED: Revert to original fetchAndUpdateItem function
export const fetchAndUpdateItem = async (
  fetchFunc: Function,
  key: string,
  excludedKey: keyof ExcludedItems,
  idKey: keyof SharePointItem,
  sharepointId: number,
  sharepoint_id: string,
  sharepoint_url: string,
  excluded: ExcludedItems,
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>,
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>,
  setIsLoadingTabs?: React.Dispatch<React.SetStateAction<{ [key: number]: { [key: string]: boolean } }>>,
  isRefresh?: boolean,
  assistant?: any
) => {
  const app_id = assistant?.app_id || '';
  
  // ORIGINAL working code - no changes needed here
  const data = await fetchFunc(sharepoint_id, sharepoint_url, app_id, isRefresh).catch(() => ({ data: [] }));
  
  if (!data) {
    console.error(`Failed to fetch ${key}`);
    if (setIsLoadingTabs) {
      setIsLoadingTabs(prev => ({
        ...prev,
        [sharepointId]: { ...prev[sharepointId], [key]: false }
      }));
    }
    return;
  }

  const updatedItems = updateSharePointItems(data, key, excluded, excludedKey, idKey);
  
  setSharepoints(prev => prev.map(s =>
    s.id === sharepointId ? { ...s, sharepointItems: { ...s.sharepointItems, [key]: updatedItems } } : s
  ));

  setCheckedItems(prevChecked => {
    const updatedCheckedItems = updatedItems
      .filter(item => item.checked)
      .map(item => item[idKey]?.toString())
      .filter((item): item is string => item !== undefined);

    const allCheckedItems = [
      ...prevChecked,
      ...updatedCheckedItems.filter(item => !prevChecked.includes(item))
    ];
    return allCheckedItems;
  });

  if (setIsLoadingTabs) {
    setIsLoadingTabs(prev => ({
      ...prev,
      [sharepointId]: { ...prev[sharepointId], [key]: false }
    }));
  }
};
export const fetchSharePointItems = async (
  sharepointId: number,
  sharepoints: SharePointData[],
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>,
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>,
  setIsLoadingTabs: React.Dispatch<React.SetStateAction<{ [key: number]: { [key: string]: boolean } }>>,
  assistant: any,
  isRefresh?: boolean,
  urlAnalysis?: URLAnalysis  // EXISTING: URL analysis parameter (already added in previous updates)
) => {
  const sharepoint = sharepoints.find(s => s.id === sharepointId);
  const sharepoint_id = sharepoint?.sharepoint_id;
  const sharepoint_url = sharepoint?.url || '';
  
  if (!sharepoint_id) {
    console.log('SharePoint ID is missing.');
    return;
  }
  
  if (!sharepoint || sharepoint.connectionState !== 'connected') return;

  setSharepoints(prev => prev.map(s =>
    s.id === sharepointId ? { ...s, isLoadingItems: true } : s
  ));

  setIsLoadingTabs(prev => ({
    ...prev,
    [sharepointId]: {
      lists: true,
      document_libraries: true,
    }
  }));

  try {
    const excluded = sharepoint.excludedItems;
    
    // Use URL analysis for conditional API calls
    const analysis = urlAnalysis || { url_type: 'site' };
    
    const promises: Promise<any>[] = [];
    
    // Conditionally fetch lists based on URL analysis
    if (URLTargetingUtils.shouldFetchLists(analysis)) {
      const targetListId = URLTargetingUtils.getTargetListId(analysis);
      console.log('🎯 Fetching lists with targeting:', targetListId ? `target: ${targetListId}` : 'all lists');
      
      promises.push(
        fetchAndUpdateItem(
          (sharepointId: string, url: string, appId: string, refresh: boolean) => 
            fetchLists(sharepointId, url, appId, refresh, targetListId),
          'lists', 
          'excluded_lists', 
          'id', 
          sharepointId, 
          sharepoint_id, 
          sharepoint_url, 
          excluded, 
          setSharepoints, 
          setCheckedItems, 
          setIsLoadingTabs, 
          isRefresh,
          assistant
        )
      );
    } else {
      console.log('🎯 Skipping lists fetch based on URL analysis');
    }
    
    // Conditionally fetch document libraries based on URL analysis
    if (URLTargetingUtils.shouldFetchLibraries(analysis)) {
      const targetLibraryId = URLTargetingUtils.getTargetLibraryId(analysis);
      console.log('🎯 Fetching libraries with targeting:', targetLibraryId ? `target: ${targetLibraryId}` : 'all libraries');
      
      promises.push(
        fetchAndUpdateItem(
          // ENHANCED: Updated function signature to support folder targeting
          (sharepointId: string, url: string, appId: string, refresh: boolean, targetLibId?: string, targetFolderPath?: string) => 
          fetchDocumentLibraries(sharepointId, url, appId, refresh, targetLibraryId),
          'document_libraries', 
          'excluded_document_libraries', 
          'id', 
          sharepointId, 
          sharepoint_id, 
          sharepoint_url, 
          excluded, 
          setSharepoints, 
          setCheckedItems, 
          setIsLoadingTabs, 
          isRefresh,
          assistant
        )
      );
    } else {
      console.log('🎯 Skipping libraries fetch based on URL analysis');
    }
    
    // Execute all API calls
    await Promise.all(promises);
    
  } catch (error) {
    console.error('Failed to fetch sharepoint items:', error);
  }
};

export const createNewSharePoint = (sharepoints: SharePointData[]) => {
  const newOrder = sharepoints.length + 1;
  const nextId = Math.max(...sharepoints.map(sharepoint => sharepoint.id), 0) + 1;
  
  return {
    id: nextId,
    sharepoint_id: '',
    url: 'https://nuwildcat.sharepoint.com',
    order: newOrder,
    sharepoint_name: `SharePoint Site ${newOrder}`,
    activeTab: 'CONNECT' as const,
    connectionState: 'idle' as const,
    selectedItems: {
      lists: false,
      document_libraries: false,
    },
    excludedItems: {
      excluded_lists: [],
      excluded_document_libraries: [],
      partial_excluded_document_libraries: [],
    },
    visibility: {
      lists: true,
      document_libraries: true,
    },
    sharepointItems: {
      lists: [],
      document_libraries: [],
    },
    isLoadingItems: false,
    error: undefined,
  };
};

export const reorderSharePoints = (
  sharepoints: SharePointData[],
  sharepointId: number,
  direction: 'up' | 'down'
): SharePointData[] => {
  const sortedSharepoints = [...sharepoints].sort((a, b) => a.order - b.order);
  const currentIndex = sortedSharepoints.findIndex((sharepoint) => sharepoint.id === sharepointId);

  if (
    (direction === 'up' && currentIndex === 0) ||
    (direction === 'down' && currentIndex === sortedSharepoints.length - 1)
  ) {
    return sharepoints;
  }

  const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  const updatedSharepoints = sortedSharepoints.map((sharepoint, index) => {
    if (index === currentIndex) {
      return { ...sharepoint, order: sortedSharepoints[newIndex].order };
    }
    if (index === newIndex) {
      return { ...sharepoint, order: sortedSharepoints[currentIndex].order };
    }
    return sharepoint;
  });

  return updatedSharepoints;
};

export const reorderSharePointsByDrag = (
  sharepoints: SharePointData[],
  dragIndex: number,
  hoverIndex: number
): SharePointData[] => {
  const copyListItems = [...sharepoints];
  const dragItemContent = copyListItems[dragIndex];
  copyListItems.splice(dragIndex, 1);
  copyListItems.splice(hoverIndex, 0, dragItemContent);
  
  return copyListItems.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
};

export const isDocumentExcludedFromLibrary = (
  documentId: string,
  libraryId: string,
  parentIds: string[],
  excludedItems: ExcludedItems
): boolean => {
  if (excludedItems.excluded_document_libraries.includes(libraryId)) {
    const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(
      lib => lib.lib_id === libraryId
    );
    
    if (partialLibrary) {
      const isExplicitlyExcluded = partialLibrary.excluded_docs.includes(documentId) ||
        parentIds.some(parentId => partialLibrary.excluded_docs.includes(parentId));
      
      return isExplicitlyExcluded;
    }
    
    return true;
  }
  
  const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(
    lib => lib.lib_id === libraryId
  );
  
  if (!partialLibrary) return false;
  
  if (partialLibrary.excluded_docs.includes(documentId)) return true;
  
  return parentIds.some(parentId => partialLibrary.excluded_docs.includes(parentId));
};

export const getLibrarySelectionStateFromExclusions = (
  libraryId: string,
  excludedItems: ExcludedItems
): 'checked' | 'unchecked' | 'indeterminate' => {
  if (excludedItems.excluded_document_libraries.includes(libraryId)) {
    const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(
      lib => lib.lib_id === libraryId
    );
    
    if (partialLibrary && partialLibrary.excluded_docs.length === 0) {
      return 'checked';
    }
    
    return 'unchecked';
  }
  
  const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(
    lib => lib.lib_id === libraryId
  );
  
  if (!partialLibrary || partialLibrary.excluded_docs.length === 0) {
    return 'checked';
  }
  
  return 'indeterminate';
};

export const isDocumentExplicitlyIncluded = (
  documentId: string,
  libraryId: string,
  parentIds: string[],
  excludedItems: ExcludedItems
): boolean => {
  if (!excludedItems.excluded_document_libraries.includes(libraryId)) {
    return false;
  }
  
  const partialLibrary = excludedItems.partial_excluded_document_libraries?.find(
    lib => lib.lib_id === libraryId
  );
  
  if (!partialLibrary) return false;
  
  const isInExcludedList = partialLibrary.excluded_docs.includes(documentId) ||
    parentIds.some(parentId => partialLibrary.excluded_docs.includes(parentId));
  
  return !isInExcludedList;
};

export const excludeDocumentFromLibrary = (
  excludedItems: ExcludedItems,
  libraryId: string,
  documentId: string
): ExcludedItems => {
  let updatedPartialExcluded = [...(excludedItems.partial_excluded_document_libraries || [])];
  
  const libraryIndex = updatedPartialExcluded.findIndex(lib => lib.lib_id === libraryId);
  
  if (libraryIndex === -1) {
    updatedPartialExcluded.push({
      lib_id: libraryId,
      excluded_docs: [documentId]
    });
  } else {
    const currentLib = updatedPartialExcluded[libraryIndex];
    const updatedExcludedDocs = [...currentLib.excluded_docs];
    
    if (!updatedExcludedDocs.includes(documentId)) {
      updatedExcludedDocs.push(documentId);
    }
    
    updatedPartialExcluded[libraryIndex] = {
      ...currentLib,
      excluded_docs: updatedExcludedDocs
    };
  }
  
  return {
    ...excludedItems,
    partial_excluded_document_libraries: updatedPartialExcluded
  };
};

export const includeDocumentInLibrary = (
  excludedItems: ExcludedItems,
  libraryId: string,
  documentId: string
): ExcludedItems => {
  let updatedPartialExcluded = [...(excludedItems.partial_excluded_document_libraries || [])];
  
  const libraryIndex = updatedPartialExcluded.findIndex(lib => lib.lib_id === libraryId);
  
  if (libraryIndex !== -1) {
    const currentLib = updatedPartialExcluded[libraryIndex];
    const updatedExcludedDocs = currentLib.excluded_docs.filter(id => id !== documentId);
    
    if (updatedExcludedDocs.length === 0) {
      updatedPartialExcluded.splice(libraryIndex, 1);
    } else {
      updatedPartialExcluded[libraryIndex] = {
        ...currentLib,
        excluded_docs: updatedExcludedDocs
      };
    }
  }
  
  return {
    ...excludedItems,
    partial_excluded_document_libraries: updatedPartialExcluded
  };
};

export const excludeEntireLibrary = (
  excludedItems: ExcludedItems,
  libraryId: string
): ExcludedItems => {
  let updatedExcludedLibraries = [...excludedItems.excluded_document_libraries];
  let updatedPartialExcluded = [...(excludedItems.partial_excluded_document_libraries || [])];
  
  if (!updatedExcludedLibraries.includes(libraryId)) {
    updatedExcludedLibraries.push(libraryId);
  }
  
  updatedPartialExcluded = updatedPartialExcluded.filter(lib => lib.lib_id !== libraryId);
  
  return {
    ...excludedItems,
    excluded_document_libraries: updatedExcludedLibraries,
    partial_excluded_document_libraries: updatedPartialExcluded
  };
};

export const includeEntireLibrary = (
  excludedItems: ExcludedItems,
  libraryId: string
): ExcludedItems => {
  const updatedExcludedLibraries = excludedItems.excluded_document_libraries.filter(
    id => id !== libraryId
  );
  
  return {
    ...excludedItems,
    excluded_document_libraries: updatedExcludedLibraries
  };
};

export const getAllChildDocumentIds = (
  items: any[],
  folderId: string
): string[] => {
  const documentIds: string[] = [];
  
  const findChildren = (itemList: any[], targetFolderId: string) => {
    for (const item of itemList) {
      if (item.id === targetFolderId && item.is_folder && item.children) {
        const collectIds = (children: any[]) => {
          children.forEach(child => {
            documentIds.push(child.id);
            if (child.is_folder && child.children) {
              collectIds(child.children);
            }
          });
        };
        collectIds(item.children);
        break;
      } else if (item.is_folder && item.children) {
        findChildren(item.children, targetFolderId);
      }
    }
  };
  
  findChildren(items, folderId);
  return documentIds;
};